import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, Search, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { adminApi } from '../services/adminApi';
import { Pagination } from '../components/Pagination';
import { COLORS } from '../../theme/colors';

const LIMIT = 30;

export function ChatsPage() {
  const [chats, setChats] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const loadChats = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getChats({
        search: search || undefined,
        status: statusFilter || undefined,
        skip: (page - 1) * LIMIT,
        limit: LIMIT
      });
      setChats(data.items);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to load chats:', error);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    loadChats();
  }, [loadChats]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadChats();
  };

  const handleDelete = async (chat) => {
    if (!window.confirm(`Delete chat "${chat.name}"?`)) return;
    try {
      await adminApi.deleteChat(chat.id);
      loadChats();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete chat');
    }
  };

  const handleDownload = async (documentId, fileType) => {
    try {
      const response = await adminApi.downloadDocument(documentId, fileType);
      const disposition = response.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename\*=UTF-8''(.+)/);
      const filename = filenameMatch ? decodeURIComponent(filenameMatch[1]) : `document.docx`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to download file');
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 24 }}>Chats</h1>

      <form onSubmit={handleSearch} style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-disabled)' }} />
            <input
              type="text"
              className="admin-input"
              placeholder="Search by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 40 }}
            />
          </div>
          <select
            className="admin-input"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            style={{ width: 150 }}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
          </select>
          <button type="submit" className="admin-btn admin-btn-secondary">Search</button>
        </div>
      </form>

      <div className="admin-card" style={{ padding: 0 }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Messages</th>
              <th>User</th>
              <th>Created</th>
              <th style={{ width: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 40 }}>Loading...</td>
              </tr>
            ) : chats.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No chats found</td>
              </tr>
            ) : (
              chats.map(chat => (
                <tr key={chat.id}>
                  <td>{chat.name}</td>
                  <td>
                    <span className={`admin-badge ${chat.status === 'completed' ? 'admin-badge-success' : 'admin-badge-warning'}`}>
                      {chat.status}
                    </span>
                  </td>
                  <td>{chat.message_count}</td>
                  <td style={{ fontSize: 12 }}>
                    {chat.user_email ? (
                      <Link to={`/admin/users?search=${encodeURIComponent(chat.user_email)}`} style={{ color: 'var(--accent)', textDecoration: 'none' }}>
                        {chat.user_email}
                      </Link>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>{chat.user_id}</span>
                    )}
                  </td>
                  <td>{new Date(chat.created_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {chat.document_id && (
                        <button
                          onClick={() => handleDownload(chat.document_id, 'translated')}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                          title="Download translated"
                        >
                          <Download size={16} color={COLORS.accent} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(chat)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                        title="Delete"
                      >
                        <Trash2 size={16} color={COLORS.danger} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
    </div>
  );
}
